import React, { FC, useEffect, useMemo } from 'react';
import { NewDataGrid, PeriodType } from '@devd-client/devd/components';
import {
  Avatar,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useWorkingAgreementDetailGql } from '../../../graphql';
import { Link } from 'react-router-dom';
import moment from 'moment';
import type { ColumnDef } from '@tanstack/react-table';
import type { WorkingAgreementDetailsItem } from '../../../helpers/home.types';

const EXCLUDED_KEYS = ['id', 'link', 'description', 'date'];

const formatLabel = (label: string) => {
  return label
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str: string) => str.toUpperCase());
};

interface SlideAgreementDrawerProps {
  openAgreementDrawer: boolean;
  handleAgreementDrawerClose: () => void;
  team: string;
  selected: PeriodType;
}

const SlideAgreementDrawer: FC<SlideAgreementDrawerProps> = ({
  openAgreementDrawer,
  handleAgreementDrawerClose,
  team,
  selected,
}) => {
  const {
    data: workingAgreementData,
    isLoading: workingAgreementLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useWorkingAgreementDetailGql(
    selected.startDate,
    selected.endDate,
    selected?.sprintId,
    team,
    20
  );

  const allData: WorkingAgreementDetailsItem[] = useMemo(
    () => workingAgreementData?.data ?? [],
    [workingAgreementData?.data]
  );

  const [dynamicFieldKeys, setDynamicFieldKeys] = React.useState<string[]>([]);

  useEffect(() => {
    if (!workingAgreementData?.data || workingAgreementData.data.length === 0) {
      return;
    }

    setDynamicFieldKeys((prevKeys) => {
      const keysSet = new Set(prevKeys);
      let hasNewKeys = false;

      workingAgreementData.data.forEach((item: WorkingAgreementDetailsItem) => {
        Object.entries(item).forEach(([key, value]) => {
          if (
            !EXCLUDED_KEYS.includes(key) &&
            value !== null &&
            value !== undefined &&
            !keysSet.has(key)
          ) {
            keysSet.add(key);
            hasNewKeys = true;
          }
        });
      });

      return hasNewKeys ? Array.from(keysSet) : prevKeys;
    });
  }, [workingAgreementData?.data]);

  const columns: ColumnDef<WorkingAgreementDetailsItem, any>[] = useMemo(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
        enableSorting: false,
        size: 320,
        cell: ({ row }: any) => {
          const { description, link } = row.original || {};
          if (!description) return '-';
          return link ? (
            <Link to={link}>
              <Text
                fontSize="sm"
                fontFamily="heading"
                color="primary.500"
                fontWeight="medium"
                textDecoration="underline"
                noOfLines={2}
              >
                {description}
              </Text>
            </Link>
          ) : (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={2}
            >
              {description}
            </Text>
          );
        },
      },
      ...dynamicFieldKeys.map(
        (key): ColumnDef<WorkingAgreementDetailsItem, any> => ({
          header: formatLabel(key),
          accessorKey: key,
          enableSorting: false,
          size: 160,
          cell: ({ row }: any) => {
            const value = row.original?.[key];
            if (value === null || value === undefined || value === '')
              return '-';

            if (key === 'updatedDate') {
              return (
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                  noOfLines={1}
                  whiteSpace="nowrap"
                >
                  {moment(value).format('DD MMM YYYY, h:mm A')}
                </Text>
              );
            }

            if (key === 'assignee') {
              return (
                <Flex align="center">
                  <Avatar size="xs" name={String(value)} />
                  <Text
                    ml={2}
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.secondary"
                    fontWeight="medium"
                    noOfLines={1}
                  >
                    {value}
                  </Text>
                </Flex>
              );
            }

            return (
              <Text
                fontSize="sm"
                fontFamily="heading"
                color="text.secondary"
                fontWeight="medium"
                noOfLines={1}
              >
                {String(value)}
              </Text>
            );
          },
        })
      ),
    ],
    [dynamicFieldKeys]
  );

  return (
    <Modal
      isOpen={openAgreementDrawer}
      onClose={handleAgreementDrawerClose}
      size="6xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader pb={2}>
          <Stack spacing={1}>
            <Text
              fontFamily="heading"
              fontSize="md"
              fontWeight="semibold"
              color="text.primary"
            >
              Agreement Exception for Team &quot;
              {team === '' ? 'Organisation' : team}&quot;
            </Text>
            <Text
              fontFamily="heading"
              fontSize="sm"
              color="text.secondary"
              fontWeight="medium"
            >
              {moment(selected.startDate).format('DD MMM')} -{' '}
              {moment(selected.endDate).format('DD MMM')}, Agreement Exception (
              {workingAgreementData?.totalDBRowCount || 0})
            </Text>
          </Stack>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {workingAgreementLoading && allData.length === 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="400px"
                maxH="400px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap={4}
                >
                  <Spinner
                    thickness="3px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="primary.500"
                    size="lg"
                  />
                  <Text
                    fontFamily="heading"
                    fontSize="sm"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Loading data...
                  </Text>
                </Flex>
              </Box>
            ) : allData.length > 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
              >
                <NewDataGrid
                  data={allData}
                  columns={columns}
                  showLoader={false}
                  skeletonLoader={false}
                  sticky="firstCol"
                  useMaxH
                  showGridLines
                  infiniteScroll
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                />
              </Box>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(SlideAgreementDrawer);
